import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { PlacedPallet, TruckSpecs, UnitSystem } from '../types';
import { toDisplayLength, toDisplayWeight, getLengthLabel, getWeightLabel } from './units';

export const generateLoadReport = (
  truck: TruckSpecs,
  pallets: PlacedPallet[],
  totalWeight: number,
  usedLength: number,
  screenshotDataUrl: string,
  unitSystem: UnitSystem
) => {
  // Fix for ESM/CDN import issues where the default export might be nested
  const jsPDFConstructor = (jsPDF as any).jsPDF || jsPDF;
  const doc = new jsPDFConstructor();
  
  // Fix for autoTable import in some ESM environments. 
  // It handles: 1. Default function export, 2. doc.autoTable attached prototype, 3. Named export fallback
  let autoTableFn = (autoTable as any).default || autoTable;
  
  if (typeof autoTableFn !== 'function') {
      // If import failed to provide a function, check if it was attached to jsPDF prototype
      if (typeof doc.autoTable === 'function') {
          // Wrap it to match signature
          autoTableFn = (d: any, options: any) => d.autoTable(options);
      } else {
           console.error("AutoTable not found. PDF might be incomplete.");
           autoTableFn = () => {}; // No-op to prevent crash
      }
  }

  const pageWidth = doc.internal.pageSize.getWidth();
  const dateStr = new Date().toLocaleDateString();

  // --- HEADER ---
  doc.setFillColor(15, 23, 42); // Slate 900
  doc.rect(0, 0, pageWidth, 40, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.text("TruckLoad 3D Report", 14, 20);
  
  // Website URL
  doc.setFontSize(12);
  doc.setTextColor(96, 165, 250); // Blue 400
  doc.text("truckload3d.com", pageWidth - 14, 12, { align: 'right' });

  // Meta Data
  doc.setFontSize(10);
  doc.setTextColor(148, 163, 184); // Slate 400
  doc.text(`Date: ${dateStr}`, pageWidth - 14, 18, { align: 'right' });
  doc.text(`Vehicle: ${truck.name}`, pageWidth - 14, 23, { align: 'right' });
  doc.text(`${truck.region} Region`, pageWidth - 14, 28, { align: 'right' });

  // --- 3D SCREENSHOT ---
  if (screenshotDataUrl) {
    try {
        // Calculate aspect ratio to fit
        const imgProps = doc.getImageProperties(screenshotDataUrl);
        const imgHeight = (imgProps.height * 180) / imgProps.width; // width 180mm
        doc.addImage(screenshotDataUrl, 'PNG', 15, 45, 180, Math.min(imgHeight, 90));
    } catch (e) {
        console.error("Error adding image to PDF", e);
    }
  }

  // --- SUMMARY STATS ---
  const statsY = 140;
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(14);
  doc.text("Load Summary", 14, statsY);

  const wLabel = getWeightLabel(unitSystem);
  const lLabel = getLengthLabel(unitSystem);

  const statsData = [
    ['Total Weight', `${toDisplayWeight(totalWeight, unitSystem)} ${wLabel} / ${toDisplayWeight(truck.maxWeight, unitSystem)} ${wLabel}`],
    ['Linear Meters (LDM)', `${usedLength.toFixed(2)} m / ${truck.length.toFixed(2)} m`],
    ['Total Items', `${pallets.length} Pallets`],
    ['Utilization', `${((usedLength / truck.length) * 100).toFixed(1)}%`]
  ];

  autoTableFn(doc, {
    startY: statsY + 5,
    head: [['Metric', 'Value']],
    body: statsData,
    theme: 'grid',
    headStyles: { fillColor: [51, 65, 85] },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 80 } }
  });

  // --- SAFETY TIPS & INSTRUCTIONS ---
  // Using autoTable to manage spacing dynamically after the stats table
  const tipsY = (doc as any).lastAutoTable ? (doc as any).lastAutoTable.finalY + 10 : statsY + 60;
  
  const safetyData = [
      ['• Verify vehicle license plates and driver ID.'],
      ['• Form-locking > Force-locking (Tight fit is safer).'],
      ['• Use anti-slip mats under all pallets.'],
      ['• Distribute weight evenly across axles.'],
      ['• Ensure cargo is secured according to local regulations (EN 12195 / DOT).']
  ];

  autoTableFn(doc, {
      startY: tipsY,
      head: [['Safety Checklist & Instructions']],
      body: safetyData,
      theme: 'plain',
      headStyles: { 
          fillColor: [185, 28, 28], // Red 700
          textColor: 255, 
          fontStyle: 'bold',
          halign: 'left'
      },
      styles: { 
          fontSize: 9, 
          cellPadding: 1.5,
          textColor: 50
      },
      alternateRowStyles: {
          fillColor: [241, 245, 249] // Slate 100
      }
  });


  // --- CARGO MANIFEST ---
  // Access finalY safely from the safety table
  const finalY = (doc as any).lastAutoTable ? (doc as any).lastAutoTable.finalY + 15 : tipsY + 40;
  doc.setFontSize(14);
  doc.setTextColor(0, 0, 0);
  doc.text("Cargo Manifest", 14, finalY);

  // Use the new 'name' property, or fallback to a friendly "Pallet X" string
  const tableData = pallets.map((p, index) => [
    `${index + 1}`, // Sequential ID: 1, 2, 3...
    p.name || `Pallet ${index + 1}`, // Use the item name or fallback
    `${toDisplayLength(p.length * 100, unitSystem)} x ${toDisplayLength(p.width * 100, unitSystem)} x ${toDisplayLength(p.height * 100, unitSystem)} ${lLabel}`,
    `${toDisplayWeight(p.weight, unitSystem)} ${wLabel}`,
    p.stackable ? 'Yes' : 'No',
    p.stackedOn ? `On #${pallets.findIndex(x => x.id === p.stackedOn) + 1}` : 'Floor'
  ]);

  autoTableFn(doc, {
    startY: finalY + 5,
    head: [['#', 'Item Name', `Dims (${lLabel})`, `Weight (${wLabel})`, 'Stackable', 'Pos']],
    body: tableData,
    theme: 'striped',
    headStyles: { fillColor: [37, 99, 235] }, // Blue
    columnStyles: { 0: { cellWidth: 15, halign: 'center' } }
  });

  // --- FOOTER ---
  const totalPages = (doc as any).internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text('Generated by TruckLoad 3D - Free Online Load Calculator', pageWidth / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });
  }

  doc.save(`TruckLoad_Plan_${Date.now()}.pdf`);
};